/**
 * Page displaying pending transaction logs in icon navigation.
 */
import { useEffect, useMemo, useState, type Dispatch, type SetStateAction } from "react";
import {
  Box,
  Button,
  Dialog,
  InputBase,
  Typography,
} from "@mui/material";
import SearchOutlinedIcon from "@mui/icons-material/SearchOutlined";
import RefreshOutlinedIcon from "@mui/icons-material/RefreshOutlined";
import { useNavigate } from "react-router-dom";
import { payloadsDB } from "../../../../configurations/DataBase/bases";
import { DeleteDialog } from "../../Dialogs/Dialogs";

type Passenger = {
  nombre?: string;
  apellido?: string;
  edad?: number;
  fecha_ingreso?: string;
  fecha_salida?: string;
  importe?: number;
  nacionalidad?: string;
  pasaporte?: string;
  telefono?: string;
};

type Transaction = {
  id: number;
  DateCreate: string;
  FrontId: string;
  PaymentInfo: {
    personas?: number;
    importe?: number;
    detalles?: Passenger[];
    [key: string]: any;
  };
  [key: string]: any;
};

type TransactionLogsProps = {
  setValue: Dispatch<SetStateAction<number>>;
  paymentMode?: boolean;
};

const SURFACE_SHADOW =
  "0px 6px 12px -6px rgba(19, 25, 39, 0.12), 0px 8px 24px -4px rgba(19, 25, 39, 0.08)";

export const TransactionLogs = ({
  setValue,
  paymentMode = false,
}: TransactionLogsProps) => {
  const navigate = useNavigate();

  const [searchTerm, setSearchTerm] = useState("");
  const [detailsSearchTerm, setDetailsSearchTerm] = useState("");
  const [pendingTransactions, setPendingTransactions] = useState<Transaction[]>([]);
  const [currentTransaction, setCurrentTransaction] = useState<Transaction | null>(null);

  const [isDetailsOpen, setIsDetailsOpen] = useState(false);
  const [isConfirmResumeOpen, setIsConfirmResumeOpen] = useState(false);
  const [isConfirmDiscardOpen, setIsConfirmDiscardOpen] = useState(false);

  const loadTransactions = async () => {
    const all = (await payloadsDB.documents.toArray()) as Transaction[];
    setPendingTransactions(all);
  };

  useEffect(() => {
    void loadTransactions();
  }, []);

  const filteredTransactions = useMemo(() => {
    const normalized = searchTerm.trim().toLowerCase();

    if (!normalized) return pendingTransactions;

    return pendingTransactions.filter((tx) => {
      const date = String(tx.DateCreate ?? "").toLowerCase();
      const frontId = String(tx.FrontId ?? "").toLowerCase();
      const id = String(tx.id ?? "").toLowerCase();

      return (
        date.includes(normalized) ||
        frontId.includes(normalized) ||
        id.includes(normalized)
      );
    });
  }, [pendingTransactions, searchTerm]);

  const filteredPassengers = useMemo(() => {
    const details = currentTransaction?.PaymentInfo?.detalles ?? [];
    const normalized = detailsSearchTerm.trim().toLowerCase();

    if (!normalized) return details;

    return details.filter((passenger) => {
      const fullName = `${passenger.nombre ?? ""} ${passenger.apellido ?? ""}`.toLowerCase();
      const passport = String(passenger.pasaporte ?? "").toLowerCase();
      return fullName.includes(normalized) || passport.includes(normalized);
    });
  }, [currentTransaction?.PaymentInfo?.detalles, detailsSearchTerm]);

  const openDetails = (tx: Transaction) => {
    setCurrentTransaction(tx);
    setDetailsSearchTerm("");
    setIsDetailsOpen(true);
  };

  const closeDetails = () => {
    setIsDetailsOpen(false);
    setCurrentTransaction(null);
    setDetailsSearchTerm("");
  };

  const resumeTransaction = async (tx: Transaction) => {
    setValue(0);
    closeDetails();
    navigate(`/Payment/PaymentForm?paymentId=${tx.FrontId}&resume=true`);
  };

  const discardTransaction = async (tx: Transaction) => {
    await payloadsDB.documents.delete(tx.id);
    setPendingTransactions((prev) => prev.filter((t) => t.id !== tx.id));
    closeDetails();
  };

  return (
    <Box
      sx={{
        ...(paymentMode
          ? {
              bgcolor: "#F6F6F6",
              height: "100%",
              minHeight: "100%",
              overflow: "auto",
              overflowX: "hidden",
              position: "relative",
              width: "100%",
              maxWidth: "100%",
              zIndex: 1,
            }
          : {
              bgcolor: "white",
              height: { xs: "100vh", md: "calc(100vh - 84px)" },
              left: 0,
              overflow: { xs: "auto", md: "hidden" },
              overflowX: "hidden",
              position: "fixed",
              top: 0,
              width: "100%",
              zIndex: 10,
            }),
      }}
    >
      <Box
        sx={{
          boxSizing: "border-box",
          margin: "0 auto",
          maxWidth: "667px",
          pb: { xs: 16, md: 4 },
          pt: { xs: "90px", md: "71px" },
          px: { xs: 2, md: 0 },
          width: "100%",
        }}
      >
        <Box sx={{ display: "flex", justifyContent: "center", mb: "25px" }}>
          <Typography
            sx={{
              borderBottom: "2px solid #00596A",
              color: "#00596A",
              fontFamily: "Inter, sans-serif",
              fontSize: "18px",
              fontWeight: 600,
              lineHeight: "20px",
              pb: "4px",
            }}
          >
            TRANSACCIONES PENDIENTES
          </Typography>
        </Box>

        <Box
          sx={{
            alignItems: "center",
            display: "grid",
            gridTemplateColumns: { xs: "minmax(0, 1fr) auto", md: "1fr auto" },
            gap: 1.5,
            mb: "15px",
            width: "100%",
          }}
        >
          <Box
            sx={{
              alignItems: "center",
              border: "1.3px solid #888",
              borderRadius: "8px",
              display: "flex",
              height: "30px",
              minWidth: 0,
              px: "12px",
              width: { xs: "100%", md: "200px" },
            }}
          >
            <InputBase
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
              placeholder="Buscar"
              sx={{
                color: "#545454",
                fontFamily: "Inter, sans-serif",
                fontSize: "14px",
                lineHeight: "20px",
                width: "100%",
              }}
            />
            <SearchOutlinedIcon sx={{ color: "#232323", fontSize: 20 }} />
          </Box>

          <Button
            onClick={() => void loadTransactions()}
            sx={{
              border: "1.5px solid #943663",
            borderRadius: "50%",
            color: "#943663",
            height: "32px",
            minWidth: "32px",
              p: "4px",
            }}
          >
            <RefreshOutlinedIcon sx={{ fontSize: 24 }} />
          </Button>
        </Box>

        <Box
          sx={{
            backgroundColor: "#F9F9F9",
            borderRadius: "8px",
            boxShadow: SURFACE_SHADOW,
            minHeight: { xs: "531px", md: "430px" },
            p: { xs: "14px 12px", md: "14px 18px" },
          }}
        >
          <Box sx={{ alignItems: "center", display: "grid", gridTemplateColumns: { xs: "82px 1fr 88px", md: "145px 1fr 104px" }, mb: "12px" }}>
            <Typography sx={{ color: "#761542", fontFamily: "Inter, sans-serif", fontSize: "16px", fontWeight: 600, lineHeight: "20px" }}>
              FECHA
            </Typography>
            <Typography sx={{ color: "#761542", fontFamily: "Inter, sans-serif", fontSize: "16px", fontWeight: 600, lineHeight: "20px" }}>
              ID
            </Typography>
            <Box />
          </Box>

          <Box sx={{ display: "flex", flexDirection: "column", gap: "12px", maxHeight: { xs: "460px", md: "340px" }, overflow: "auto", pr: { xs: 0, md: 1 } }}>
            {filteredTransactions.map((tx) => (
              <Box key={tx.id} sx={{ alignItems: "center", display: "grid", gap: "8px", gridTemplateColumns: { xs: "82px 1fr 88px", md: "145px 1fr 104px" } }}>
                <Typography sx={{ color: "#232323", fontFamily: "Inter, sans-serif", fontSize: { xs: "12px", md: "14px" }, fontWeight: 400, lineHeight: "18px" }}>
                  {String(tx.DateCreate ?? "").slice(0, 10) || "-"}
                </Typography>
                <Typography sx={{ color: "#232323", fontFamily: "Inter, sans-serif", fontSize: { xs: "12px", md: "14px" }, fontWeight: 400, lineHeight: "18px", overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}>
                  {tx.FrontId || tx.id}
                </Typography>
                <Button
                  onClick={() => openDetails(tx)}
                  sx={{
                    bgcolor: "#943663",
                    borderRadius: "24px",
                    color: "#F9F9F9",
                    fontFamily: "Inter, sans-serif",
                    fontSize: { xs: "9px", md: "10px" },
                    fontWeight: 600,
                    height: { xs: "24px", md: "22px" },
                    lineHeight: "11px",
                    minWidth: { xs: "88px", md: "74px" },
                    px: { xs: "6px", md: "8px" },
                    textTransform: "none",
                    "&:hover": { bgcolor: "#7f2e56" },
                  }}
                >
                  Ver detalle
                </Button>
              </Box>
            ))}

            {filteredTransactions.length === 0 && (
              <Typography sx={{ color: "#545454", fontFamily: "Inter, sans-serif", fontSize: "14px", lineHeight: "20px", py: 2, textAlign: "center" }}>
                No hay transacciones pendientes.
              </Typography>
            )}
          </Box>
        </Box>
      </Box>

      <Dialog
        open={isDetailsOpen}
        onClose={closeDetails}
        maxWidth={false}
        PaperProps={{
          sx: {
            backgroundColor: "#F9F9F9",
            borderRadius: "8px",
            boxShadow: SURFACE_SHADOW,
            height: "339px",
            m: 2,
            overflow: "hidden",
            width: "529px",
          },
        }}
      >
        {currentTransaction && (
          <Box sx={{ display: "flex", flexDirection: "column", gap: "20px", height: "100%", p: "24px" }}>
            <Typography sx={{ color: "#761542", fontFamily: "Inter, sans-serif", fontSize: "18px", fontWeight: 600, lineHeight: "20px", textAlign: "center" }}>
              DETALLE DE LA TRANSACCIÓN
            </Typography>

            <Box sx={{ alignItems: "center", display: "flex", gap: "65px", justifyContent: "center" }}>
              <Typography sx={{ color: "#232323", fontFamily: "Inter, sans-serif", fontSize: "14px", lineHeight: "20px" }}>
                Fecha: <Box component="span" sx={{ fontWeight: 600 }}>{String(currentTransaction.DateCreate ?? "").slice(0, 10) || "-"}</Box>
              </Typography>
              <Typography sx={{ color: "#232323", fontFamily: "Inter, sans-serif", fontSize: "14px", lineHeight: "20px" }}>
                Personas: <Box component="span" sx={{ fontWeight: 600 }}>{currentTransaction.PaymentInfo?.personas ?? 0}</Box>
              </Typography>
              <Typography sx={{ color: "#232323", fontFamily: "Inter, sans-serif", fontSize: "14px", lineHeight: "20px" }}>
                Importe: <Box component="span" sx={{ fontWeight: 600 }}>${currentTransaction.PaymentInfo?.importe ?? 0}</Box>
              </Typography>
            </Box>

            <Box
              sx={{
                alignItems: "center",
                border: "1.3px solid #888",
                borderRadius: "8px",
                display: "flex",
                height: "30px",
                px: "12px",
                width: "200px",
              }}
            >
              <InputBase
                value={detailsSearchTerm}
                onChange={(e) => setDetailsSearchTerm(e.target.value)}
                placeholder="Buscar"
                sx={{
                  color: "#545454",
                  fontFamily: "Inter, sans-serif",
                  fontSize: "14px",
                  lineHeight: "20px",
                  width: "100%",
                }}
              />
              <SearchOutlinedIcon sx={{ color: "#232323", fontSize: 20 }} />
            </Box>

            <Box sx={{ alignItems: "center", display: "grid", gridTemplateColumns: "1.45fr 1fr .7fr 1.1fr 1.1fr", width: "100%" }}>
              <Typography sx={{ color: "#761542", fontFamily: "Inter, sans-serif", fontSize: "12px", fontWeight: 600, lineHeight: "11px" }}>NOMBRE</Typography>
              <Typography sx={{ color: "#761542", fontFamily: "Inter, sans-serif", fontSize: "12px", fontWeight: 600, lineHeight: "11px" }}>PASAPORTE</Typography>
              <Typography sx={{ color: "#761542", fontFamily: "Inter, sans-serif", fontSize: "12px", fontWeight: 600, lineHeight: "11px" }}>IMPORTE</Typography>
              <Typography sx={{ color: "#761542", fontFamily: "Inter, sans-serif", fontSize: "12px", fontWeight: 600, lineHeight: "11px" }}>INGRESO</Typography>
              <Typography sx={{ color: "#761542", fontFamily: "Inter, sans-serif", fontSize: "12px", fontWeight: 600, lineHeight: "11px" }}>SALIDA</Typography>
            </Box>

            <Box sx={{ borderBottom: "1px solid #00596A", mt: "-10px" }} />

            <Box sx={{ display: "flex", flexDirection: "column", gap: "8px", maxHeight: "95px", overflow: "auto", pr: 0.5 }}>
              {filteredPassengers.map((passenger, idx) => (
                <Box key={`${passenger.pasaporte ?? "pass"}-${idx}`} sx={{ alignItems: "center", display: "grid", gridTemplateColumns: "1.45fr 1fr .7fr 1.1fr 1.1fr", gap: "22px" }}>
                  <Typography sx={{ color: "#000", fontFamily: "Inter, sans-serif", fontSize: "14px", lineHeight: "20px" }}>{`${passenger.nombre ?? ""} ${passenger.apellido ?? ""}`.trim() || "-"}</Typography>
                  <Typography sx={{ color: "#000", fontFamily: "Inter, sans-serif", fontSize: "14px", lineHeight: "20px" }}>{passenger.pasaporte ?? "-"}</Typography>
                  <Typography sx={{ color: "#000", fontFamily: "Inter, sans-serif", fontSize: "14px", lineHeight: "20px" }}>{passenger.importe ?? 0}</Typography>
                  <Typography sx={{ color: "#000", fontFamily: "Inter, sans-serif", fontSize: "14px", lineHeight: "20px" }}>{passenger.fecha_ingreso ?? "-"}</Typography>
                  <Typography sx={{ color: "#000", fontFamily: "Inter, sans-serif", fontSize: "14px", lineHeight: "20px" }}>{passenger.fecha_salida ?? "-"}</Typography>
                </Box>
              ))}

              {filteredPassengers.length === 0 && (
                <Typography sx={{ color: "#545454", fontFamily: "Inter, sans-serif", fontSize: "14px", lineHeight: "20px", textAlign: "center" }}>
                  Sin pasajeros para mostrar.
                </Typography>
              )}
            </Box>

            <Box sx={{ display: "flex", gap: "26px", justifyContent: "center", mt: "auto" }}>
              <Button
                onClick={() => setIsConfirmDiscardOpen(true)}
                sx={{
                  border: "1.5px solid #943663",
                  borderRadius: "24px",
                  color: "#943663",
                  fontFamily: "Inter, sans-serif",
                  fontSize: "10px",
                  fontWeight: 600,
                  height: "24px",
                  lineHeight: "11px",
                  minWidth: "140px",
                  px: "8px",
                  textTransform: "none",
                }}
              >
                Descartar transacción
              </Button>
              <Button
                onClick={closeDetails}
                sx={{
                  border: "1.5px solid #943663",
                  borderRadius: "24px",
                  color: "#943663",
                  fontFamily: "Inter, sans-serif",
                  fontSize: "10px",
                  fontWeight: 600,
                  height: "24px",
                  lineHeight: "11px",
                  minWidth: "63px",
                  px: "8px",
                  textTransform: "none",
                }}
              >
                Cerrar
              </Button>
              <Button
                onClick={() => setIsConfirmResumeOpen(true)}
                sx={{
                  bgcolor: "#943663",
                  borderRadius: "24px",
                  color: "#F9F9F9",
                  fontFamily: "Inter, sans-serif",
                  fontSize: "10px",
                  fontWeight: 600,
                  height: "24px",
                  lineHeight: "11px",
                  minWidth: "133px",
                  px: "8px",
                  textTransform: "none",
                  "&:hover": { bgcolor: "#7f2e56" },
                }}
              >
                Retomar transacción
              </Button>
            </Box>
          </Box>
        )}
      </Dialog>

      <DeleteDialog
        tittle="Descartar transacción"
        open={isConfirmDiscardOpen}
        setOpen={setIsConfirmDiscardOpen}
        setsavingchanges={() => discardTransaction(currentTransaction!)}
        question="¿Deseas descartar esta transacción?"
      />

      <DeleteDialog
        tittle="Retomar transacción"
        open={isConfirmResumeOpen}
        setOpen={setIsConfirmResumeOpen}
        setsavingchanges={() => resumeTransaction(currentTransaction!)}
        question="¿Deseas retomar esta transacción?"
      />
    </Box>
  );
};
